import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { getFormValues, getFormInitialValues } from "redux-form";
import Cropper from "react-easy-crop";
import { isEqual } from "lodash";

import ToggleField from "widgets/ToggleField";
import useCropMetadata from "hooks/useCropMetadata";
import useRefinementDataUrl from "hooks/useRefinementDataUrl";
import { buildAssetUrl } from "utils/Assets";
import { EDIT_VERSION_BLADE_FORM } from "forms/list";
import { updateCropMetadata } from "reducers/Refinements";
import { translateOffsetsFromEasyCrop, translateOffsetsToEasyCrop } from "utils/Refinements";

import "./AdjustFramingDetailPanel.css";

export default function AdjustFramingDetailPanel(props) {
	const {
			activeVariableName,
			libraryId,
		} = props
		, dispatch = useDispatch()
		, initialValues = useSelector(getFormInitialValues(EDIT_VERSION_BLADE_FORM)) || {}
		, values = useSelector(getFormValues(EDIT_VERSION_BLADE_FORM)) || {}
		, initialVariableInfo = initialValues[activeVariableName] ?? {}
		, variableInfo = values[activeVariableName] ?? {}
		, assetUrl = variableInfo.assetUrl ?? (activeVariableName ? buildAssetUrl(libraryId, variableInfo.data) : '')
		, {
			assetId,
			cropMetadata = {},
		} = variableInfo
		, { cropMetadata: cropMetadataInit = {} } = initialVariableInfo
		, {
			canvas = {},
			frame = {},
			asset = {},
			isAutoSmartFramable,
		} = cropMetadata
		, {
			left = 0,
			top = 0,
			width: canvasWidth = 1,
			height: canvasHeight = 1,
		} = canvas
		, {
			width: frameWidth = 1,
			height: frameHeight = 1,
		} = frame
		, {
			width: assetWidth = 1,
			height: assetHeight = 1,
		} = asset
		, [useCrop, setUseCrop] = useState(false)
		, [croppedVariableChanged, setCroppedVariableChanged] = useState(false)
		, hasCanvas = canvas?.width && canvas?.width !== 1 && canvas?.height !== 1
		, { cropMetadata: autoCropMetaData } = useCropMetadata({
			assetId,
			frameWidth,
			frameHeight,
			// We want to skip expensive call to adStusio when we do not need it
			skip: (!isAutoSmartFramable && !useCrop) || hasCanvas || !croppedVariableChanged,
		})
		, [hideImageReference, setHideImageReference] = useState(false)
		, crop = translateOffsetsToEasyCrop({
			left,
			top,
			frameWidth,
			frameHeight,
			canvasWidth,
			canvasHeight,
			assetWidth,
		})
		, cropSize = { width: frameWidth, height: frameHeight }
		, disableSmartFrame = !variableInfo || useCrop || hideImageReference
		, disableReset = !useCrop && !crop
		, disableHideImageReference = !crop
		, zoom = canvasWidth / assetWidth
		, offsets = translateOffsetsFromEasyCrop({
			x: crop?.x,
			y: crop?.y,
			zoom,
			frameWidth,
			frameHeight,
			assetWidth,
			assetHeight,
		})
		, { isLoading, refinementDataUrl } = useRefinementDataUrl({
			src: assetUrl,
			left: offsets.left,
			top: offsets.top,
			frameWidth,
			frameHeight,
			canvasWidth,
			canvasHeight,
			opacity: 1,
			// Only does real hard lifting of rendering in canvas when hideImageReference turns true.
			skip: !hideImageReference,
		})
		, style = {
			cropAreaStyle: { border: "2px solid rgba(255, 255, 255, 0.5)" },
			mediaStyle: { maxHeight: 'none', maxWidth: 'none', position: "relative" },
		} // Custom style for the crop area which library exposes
		, [xFocal, setXFocal] = useState(0)
		, [yFocal, setYFocal] = useState(0)
		, [zooming, setZooming] = useState(false)
		, [interactionEnd, setInteractionEnd] = useState(false);

	useEffect(() => {
		if (activeVariableName) {
			setUseCrop(hasCanvas);
			setHideImageReference(false);
			setCroppedVariableChanged(true);
			if (hasCanvas) {
				setXFocal(crop.x / zoom);
				setYFocal(crop.y / zoom);
			}
		}
	}, [activeVariableName, assetId]);

	function handleMediaLoaded() {
		setCroppedVariableChanged(false);
	}


	useEffect(() => {
		const shouldAutoCrop = useCrop && !hasCanvas && autoCropMetaData;
		if (shouldAutoCrop) {
			dispatch(updateCropMetadata(activeVariableName, values, autoCropMetaData.canvas, false));
		}
	}, [autoCropMetaData, hasCanvas, useCrop]);

	// Turns smart framing on
	const handleSmartFrame = () => {
		if (!disableSmartFrame) {
			setUseCrop(true);
			if (!hasCanvas && autoCropMetaData?.canvas?.width) {
				dispatch(updateCropMetadata(activeVariableName, values, autoCropMetaData.canvas, false));
			}
		}
	};

	// Resets cropped image turns smart framing off
	const handleReset = () => {
		if (!disableReset) {
			setHideImageReference(false);
			if (!cropMetadataInit.canvas) {
				setUseCrop(false);
			}
			dispatch(updateCropMetadata(activeVariableName, values, cropMetadataInit.canvas, cropMetadataInit.hasManualRefinements));
		}
	};

	// Hides image referenece and only shows cropped image
	const handleHideImageReference = () => {
		if (!disableHideImageReference) {
			setHideImageReference(!hideImageReference);
		}
	};

	const onZoomStart = () => {
		setZooming(true);
		// We need to return true to allow zooming to happen
		// otherwise this method preventsdefault and stops propagation
		return true;
	};

	const onZoom = (value) => {
		//We want to keep focal/central point of crop in the same place in reference to image
		//regardless of zooming so we need to adjust x and y during zooming
		const offsetsNew = translateOffsetsFromEasyCrop({
			x: xFocal * value,
			y: yFocal * value,
			zoom: value,
			frameWidth,
			frameHeight,
			assetWidth,
			assetHeight,
		});
		dispatch(updateCropMetadata(activeVariableName, values,
			{ ...offsetsNew, width: assetWidth * value, height: assetHeight * value },
			true));
	};

	const handleInteractionEnd = () => {
		setZooming(false);
		setInteractionEnd(true);
	};

	const handleCropChange = (cropNew) => {
		// Only do this when image reference is not hidden and we are using crop otherwise distortions happen
		// Also there are problems during switch from one variable to another
		if (interactionEnd) {
			return;
		}
		const { x, y } = cropNew
			, handleChange = !hideImageReference
				&& !zooming
				&& hasCanvas;
		if (handleChange) {
			// We want to debounce the focal point change so we don't have to update it on every crop change
			// it's expensive and distorts focal point due to racing and listeners setup in library
			setXFocal(x / zoom);
			setYFocal(y / zoom);

			const offsets = translateOffsetsFromEasyCrop({
				x: cropNew.x,
				y: cropNew.y,
				zoom,
				frameWidth,
				frameHeight,
				assetWidth,
				assetHeight,
			});
			const newCropMetadata = {
				...offsets,
				width: assetWidth * zoom,
				height: assetHeight * zoom,
			};
			if (!isEqual(newCropMetadata, cropMetadata.canvas)) {
				dispatch(updateCropMetadata(activeVariableName, values, newCropMetadata, true));
			}
		}
	};

	let img = null;
	if (variableInfo) {
		if (!useCrop) {
			img = (<img src={assetUrl}
				alt={variableInfo.name} />);
		} else if (hideImageReference && !isLoading) {
			img = (<img
				src={refinementDataUrl}
				alt={variableInfo.name} />);
		} else {
			img = (<Cropper
				crop={crop}
				cropSize={cropSize}
				image={assetUrl}
				maxZoom={10}
				minZoom={0.1}
				onCropChange={handleCropChange}
				onZoomChange={onZoom}
				onWheelRequest={onZoomStart}
				onInteractionEnd={handleInteractionEnd}
				onMediaLoaded={handleMediaLoaded}
				onInteractionStart={() => setInteractionEnd(false)}
				showGrid={false}
				style={style}
				zoom={zoom}
				restrictPosition={false}
			/>);
		}
	}

	const smartFrameClass = `${disableSmartFrame ? "disabled-toolbar-action" : ""} cypress-open-smart-framing`
		, resetFrameClass = `${disableReset ? "disabled-toolbar-action" : ""} cypress-reset-frame`
		, hideImageReferenceClass = `${disableHideImageReference ? "disabled-toolbar-action" : ""} cypress-hide-image-reference`;

	return (<div className="adjust-framing-detail">
		<div className="header">
			<div onClick={handleSmartFrame} className={smartFrameClass}>
				<i className="fas fa-expand" />Smart Frame</div>
			<div onClick={handleReset} className={resetFrameClass}>
				<i className="fas fa-undo" />Reset</div>
			<div onClick={handleHideImageReference} className={hideImageReferenceClass}>
				<ToggleField
					label="Hide Image Reference"
					input={{
						onChange: handleHideImageReference,
						value: hideImageReference,
					}}
				/>
			</div>
		</div>
		<div className="content">{img}</div>
	</div>);
}

AdjustFramingDetailPanel.displayName = "AdjustFramingDetailPanel";
AdjustFramingDetailPanel.propTypes = {
	activeVariableName: PropTypes.string,
	libraryId: PropTypes.oneOfType([
		PropTypes.number,
		PropTypes.string,
	]),
};
